import AnswerUtil from './answer-util';
import ImmutableRenderComponent from './immutable-render-component';
import QuestionUtil from './question-util';

import React from 'react';

/**
 * Component that represents the Answer Cards displayed on the Answer Page
 * under "Aristo Answered" and "Other Answers" headings.
 */
export default class AnswerCard extends ImmutableRenderComponent {
   constructor(props) {

     super(props);
     this.state = { open: this.props.initialStateOpened };
   }

   static get propTypes() {
    return {
      correctAnswer: React.PropTypes.any,
      question: React.PropTypes.string.isRequired,
      answer: React.PropTypes.object.isRequired,
      initialStateOpened: React.PropTypes.bool.isRequired,
      confident: React.PropTypes.bool.isRequired,
    };
  }

  /**
   * Callback that toggles the state of the answer card when Explain/Hide button is clicked.
   */
  onExplainHideClicked() {
    this.setState({ open: !this.state.open });
  }

  /**
   * Method to render the Explain/Hide button on the answer card based on the 'open' state
   * of AnswerCard and the title as either the answer choice, when the card is closed, or
   * the question text, if the card if open.
   */
  renderTitle() {
    // Jsx to show (repeated) question without answer choices in answer card.
    const renderedQuestion = QuestionUtil.renderQuestion(this.props.question, false);

    // Answer Choice Text is shown as title when card is closed. This has the
    // answer choice text label in parens next to the answer text.
    const answerChoiceText = this.props.answer.selection.multipleChoice ?
      AnswerUtil.formatMCChoiceLabel(this.props.answer.selection.multipleChoice.key) + " " + this.props.answer.selection.multipleChoice.focus :
      this.props.answer.selection.directAnswer.answer;

    // Set main contents of the answer card title to return.
    const title = (this.state.open ?
      (<div className="answer-card-title">
         <span className="question-repeated"><strong>Question:</strong> {renderedQuestion}</span>
         {this.props.correctAnswer ? null : <span className="explain expanded">Hide</span>}
       </div>) :
      (<div className="answer-card-title">
         <span className="answer-choice-as-title">{answerChoiceText}</span>
         <span className="explain">Explain</span>
       </div>)
    );

    return (
      <a className="details-title-link" onClick={this.onExplainHideClicked.bind(this)}>
        {title}
      </a>);
  }

  /**
   * Method to render answer details based on the solver the answer comes from.
   * If there is an explanation with a diagram, we prioritize and show that.
   */
  renderAnswerDetails() {
    const bestAnalyses = AnswerUtil.bestAnalyses(this.props.answer);
    const answerDetails = (bestAnalyses.map((analysisHolder, i) => {
      const analysis = analysisHolder.analysis;
      const explanationJsx = (analysis.explanationDiagramURL === undefined) ?
      [
        <p key="1"><strong>Because:</strong> {analysis.explanation}</p>,
        <p key="2"><strong>Confidence:</strong> {AnswerUtil.scoreAsPercentage(this.props.answer.confidence, 2)}</p>,
        <p key="3"><strong>Source:</strong> {analysis.sourceFriendlyName}</p>,
      ] : [
        (<p key="1"><strong>Because:</strong>
           <div className="indent">
             <span className="because-subtitle">Diagram elements detected:</span>
             <br>
               <image className="diagram" src={analysis.explanationDiagramURL}/>
             </br>
           </div>
           <div className="indent">
             <p dangerouslySetInnerHTML={{__html: analysis.concmodExplanationHTML}}/>
           </div>
        </p>),
      ];
      return (<div className="support" key={i}>
         <p><strong>Aristo's Answer:</strong> {AnswerUtil.formatMCChoiceLabel(this.props.answer.selection.multipleChoice.key)} {this.props.answer.selection.multipleChoice.focus}</p>
         {explanationJsx}
       </div>);
    }));
    return answerDetails;
  }

  /**
   * Render the confidence line with different styling for low-confidence answers
   */
  renderConfidence(confidence, meetsThreshold) {
    const formattedScore = AnswerUtil.scoreAsPercentage(confidence, 2);
    const scoreText = !meetsThreshold ?
      <span className="red-confidence">{formattedScore}</span> : formattedScore;
    return (<p><strong>Confidence:</strong> {scoreText}</p>);
  }

  /**
   * Render MC answer details (internal demo)
   */
  renderAnswerDetailsMC() {
    const bestAnalyses = AnswerUtil.bestAnalyses(this.props.answer);

    if (bestAnalyses.length > 0 && bestAnalyses[0].analysis.concmodExplanationHTML) {
      return this.renderAnswerDetails();
    }

    // Guard against bad long "sentences".
    const maxJustificationLength = 500;
    function trimJustificationText(justification) {
      if (justification) {
        var trimmedString = justification.substring(0, maxJustificationLength);
        if (trimmedString.length < justification.length) {
          return trimmedString + "...";
        }
      }
      return justification;
    }

    // Helper to get the label text for displaying solver explanations.
    function  getExplanationLabelFormatted(solverData) {
      var explanationLabelFormatted = null;
      const explanationLabel = solverData[0].explanationLabel;
      if (explanationLabel) {
        explanationLabelFormatted = explanationLabel + ":";
      }
      return explanationLabelFormatted;
    }

    const solverAnalyses = AnswerUtil.solverAnalyses(this.props.answer, this.props.demoSolvers.filter((x) => x.displayName));

    // TODO: Add proper CSS formatting
    const solverScores = solverAnalyses.map(function(solverAnalysis, i) {
      const solverData = this.props.demoSolvers.filter(function(solver) { return solver.displayName === solverAnalysis.displayName; });
      const moreInfoLink = solverData[0].paperURL ? <span className="more-info-link"><a href={solverData[0].paperURL} target="_blank">More Info</a></span> : null;
      return (
        <div key={i}>
          <p title={solverAnalysis.solver}><small><strong>{solverAnalysis.displayName}:</strong> {AnswerUtil.scoreAsPercentage(solverAnalysis.analysis.confidence, 2)} {moreInfoLink}</small></p>
          <p className="internal-mode-answer-justification"><small><strong>{getExplanationLabelFormatted(solverData)}</strong> {trimJustificationText(AnswerUtil.getExplanationText(solverAnalysis.analysis))}</small></p>
        </div>
      );
    }, this);

    const comment = this.props.confident ? null : "Aristo is not sure about this one...";
    const answerTitle = this.props.confident ? "Aristo's Answer:" : "Aristo's best guess:";

    const correctAnswerLabel = this.props.correctAnswer ? (
      <p>
        <strong>Correct Answer:</strong> {this.props.correctAnswer}
      </p>
    ) : null;

    return (<div className="support">
      <p className="answer-comment">{comment}</p>
      <p><strong>{answerTitle}</strong> {AnswerUtil.formatMCChoiceLabel(this.props.answer.selection.multipleChoice.key)} {this.props.answer.selection.multipleChoice.focus}</p>
      {correctAnswerLabel}
      {this.renderConfidence(this.props.answer.confidence , this.props.confident)}
      <div>as computed from these reasoners:<br/></div>
      <div className="solver-scores">{solverScores}</div>
      </div>);
  }

  /**
   * Render DA answer details
   */
  renderAnswerDetailsDA() {
    const comment = this.props.confident ? null : "Aristo is not sure about this one...";
    const answerTitle = this.props.confident ? "Aristo's Answer:" : "Aristo's best guess:";

    // TODO: More sensible scoring?
    const daScore = Math.min(this.props.answer.confidence, 1.0) * 0.6;
    const explanationJsx = this.renderConfidence(daScore , this.props.confident);

    return (<div className="support">
      <p className="answer-comment">{comment}</p>
      <p><strong>{answerTitle}</strong> {this.props.answer.selection.directAnswer.answer} </p>
      {explanationJsx}
    </div>);
  }

  /**
   * Method to render actual contents of Answer Card: if in 'open' state show all details
   * like Question, Answer, Confidence, etc.
   */
  renderAnswer() {
    return (
      <div className="contents">
        {this.props.answer.selection.multipleChoice ?
          this.renderAnswerDetailsMC() :
          this.renderAnswerDetailsDA()}
      </div>);
  }

  /**
   * Method to render answer card based on current answer (passed down as property).
   */
  renderAnswerCard() {
    return (
      this.state.open ?
        (<div>
           <div className="details-title expanded">
              {this.renderTitle()}
           </div>
           {this.renderAnswer()}
         </div>)
      : (<div>
           <div className="details-title">
             {this.renderTitle()}
           </div>
         </div>)
    );
  }

  /**
   * Renders relevant parts of an answer passed as a property.
   */
  render() {
    return(
      <div className="answer-details">
        {this.renderAnswerCard()}
      </div>
    );
  }
}
